import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { UserData } from "~/types/global";

interface UserActions {
  setUser: (user: UserData) => void;
  setUserSalt: (salt: string | null) => void;
  setIsVerified: (isVerified: boolean) => void;
  setHasAcceptedTerms: (hasAcceptedTerms: boolean) => void;
  setUserCredits: (credits: number) => void;
  setAiModel: (aiModel: string) => void;
  clearUser: () => void;
}

interface UserState {
  userData: UserData | null;
  useCredits: number;
  userSalt: string | null;
  isVerified: boolean;
  hasAcceptedTerms: boolean;
  actions: UserActions;
}

const useUserStore = create<UserState>()(
  devtools(
    persist(
      (set) => ({
        userData: null,
        useCredits: 0,
        userSalt: null,
        isVerified: false,
        hasAcceptedTerms: false,
        actions: {
          setUser: (user: UserData) => set({ userData: user }),
          setUserCredits: (credits: number) => set({ useCredits: credits }),
          setUserSalt: (salt: string | null) => set({ userSalt: salt }),
          setIsVerified: (isVerified: boolean) => set({ isVerified }),
          setHasAcceptedTerms: (hasAcceptedTerms: boolean) => set({ hasAcceptedTerms }),
          setAiModel: (aiModel: string) =>
            set((state) => ({
              userData: state.userData ? { ...state.userData, ai_model: aiModel } : null,
            })),
          clearUser: () => set({ userData: null, userSalt: null, isVerified: false, hasAcceptedTerms: false }),
        },
      }),
      {
        name: "report-user",
        partialize: (state) => ({
          userData: state.userData,
          useCredits: state.useCredits,
          userSalt: state.userSalt,
        }),
      }
    )
  )
);

export const useUserData = () => useUserStore((state) => state.userData)
export const useUserSalt = () => useUserStore((state) => state.userSalt)
export const useUserIsVerified = () => useUserStore((state) => state.isVerified)
export const useUserHasAcceptedTerms = () => useUserStore((state) => state.hasAcceptedTerms);
export const useUserActions = () => useUserStore((state) => state.actions);
export const useUserCredits = () => useUserStore((state) => state.useCredits);
export const getUserAiModel = () => useUserStore.getState().userData?.ai_model ?? "local";
